import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface WidgetType {
  id: string
  name: string
  description: string
  icon: string
  config_fields: string[]
}

interface Metric {
  id: string
  name: string
  format: string
  category: string
}

interface DataSource {
  id: string
  name: string
  description: string
}

interface WidgetSize {
  id: string
  name: string
  cols: number
  rows: number
}

interface Widget {
  id: string
  name: string
  widget_type: string
  config: Record<string, unknown>
  size: string
  data?: Record<string, unknown>
}

interface Dashboard {
  id: string
  name: string
  description?: string
  is_default: boolean
  widgets: Array<{ widget_id: string; position: { x: number; y: number } }>
  widgets_data?: Widget[]
}

interface WidgetTemplate {
  id: string
  name: string
  widget_type: string
  config: Record<string, unknown>
  size: string
}

const API_BASE = '/api/widget-builder'

export default function WidgetBuilder() {
  const [widgetTypes, setWidgetTypes] = useState<WidgetType[]>([])
  const [metrics, setMetrics] = useState<Metric[]>([])
  const [dataSources, setDataSources] = useState<DataSource[]>([])
  const [widgetSizes, setWidgetSizes] = useState<WidgetSize[]>([])
  const [templates, setTemplates] = useState<WidgetTemplate[]>([])
  const [widgets, setWidgets] = useState<Widget[]>([])
  const [dashboards, setDashboards] = useState<Dashboard[]>([])
  const [selectedDashboard, setSelectedDashboard] = useState<Dashboard | null>(null)
  const [isCreatingWidget, setIsCreatingWidget] = useState(false)
  const [isCreatingDashboard, setIsCreatingDashboard] = useState(false)
  const [activeTab, setActiveTab] = useState<'widgets' | 'dashboards' | 'templates'>('widgets')

  // Widget creation form
  const [newWidget, setNewWidget] = useState({
    name: '',
    widget_type: '',
    size: 'medium',
    config: {} as Record<string, unknown>
  })

  // Dashboard creation form
  const [newDashboard, setNewDashboard] = useState({
    name: '',
    description: '',
    is_default: false
  })

  useEffect(() => {
    fetchConfiguration()
    fetchUserWidgets()
    fetchUserDashboards()
  }, [])

  const fetchConfiguration = async () => {
    try {
      const [typesRes, metricsRes, sourcesRes, sizesRes, templatesRes] = await Promise.all([
        fetch(`${API_BASE}/widget-types`),
        fetch(`${API_BASE}/metrics`),
        fetch(`${API_BASE}/data-sources`),
        fetch(`${API_BASE}/widget-sizes`),
        fetch(`${API_BASE}/templates`)
      ])

      setWidgetTypes(await typesRes.json())
      setMetrics(await metricsRes.json())
      setDataSources(await sourcesRes.json())
      setWidgetSizes(await sizesRes.json())
      setTemplates(await templatesRes.json())
    } catch (error) {
      console.error('Failed to fetch configuration:', error)
    }
  }

  const fetchUserWidgets = async () => {
    try {
      const res = await fetch(`${API_BASE}/widgets/user/all`)
      const data = await res.json()
      setWidgets(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Failed to fetch widgets:', error)
    }
  }

  const fetchUserDashboards = async () => {
    try {
      const res = await fetch(`${API_BASE}/dashboards/user/all`)
      const data = await res.json()
      setDashboards(Array.isArray(data) ? data : [])
    } catch (error) {
      console.error('Failed to fetch dashboards:', error)
    }
  }

  const createWidget = async () => {
    try {
      const res = await fetch(`${API_BASE}/widgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newWidget)
      })
      const widget = await res.json()
      if (!widget.error) {
        setWidgets([...widgets, widget])
        setNewWidget({ name: '', widget_type: '', size: 'medium', config: {} })
        setIsCreatingWidget(false)
      }
    } catch (error) {
      console.error('Failed to create widget:', error)
    }
  }

  const createWidgetFromTemplate = async (template: WidgetTemplate) => {
    try {
      const res = await fetch(`${API_BASE}/widgets/from-template`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ template_id: template.id })
      })
      const widget = await res.json()
      if (!widget.error) {
        setWidgets([...widgets, widget])
      }
    } catch (error) {
      console.error('Failed to create widget from template:', error)
    }
  }

  const deleteWidget = async (widgetId: string) => {
    try {
      const res = await fetch(`${API_BASE}/widgets/${widgetId}?user_id=default_user`, {
        method: 'DELETE'
      })
      const result = await res.json()
      if (result.success) {
        setWidgets(widgets.filter(w => w.id !== widgetId))
      }
    } catch (error) {
      console.error('Failed to delete widget:', error)
    }
  }

  const createDashboard = async () => {
    try {
      const res = await fetch(`${API_BASE}/dashboards`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(newDashboard)
      })
      const dashboard = await res.json()
      if (!dashboard.error) {
        setDashboards([...dashboards, dashboard])
        setNewDashboard({ name: '', description: '', is_default: false })
        setIsCreatingDashboard(false)
      }
    } catch (error) {
      console.error('Failed to create dashboard:', error)
    }
  }

  const deleteDashboard = async (dashboardId: string) => {
    try {
      const res = await fetch(`${API_BASE}/dashboards/${dashboardId}?user_id=default_user`, {
        method: 'DELETE'
      })
      const result = await res.json()
      if (result.success) {
        setDashboards(dashboards.filter(d => d.id !== dashboardId))
        if (selectedDashboard?.id === dashboardId) {
          setSelectedDashboard(null)
        }
      }
    } catch (error) {
      console.error('Failed to delete dashboard:', error)
    }
  }

  const loadDashboard = async (dashboardId: string) => {
    try {
      const res = await fetch(`${API_BASE}/dashboards/${dashboardId}`)
      const dashboard = await res.json()
      if (!dashboard.error) {
        setSelectedDashboard(dashboard)
      }
    } catch (error) {
      console.error('Failed to load dashboard:', error)
    }
  }

  const addWidgetToDashboard = async (widgetId: string) => {
    if (!selectedDashboard) return
    try {
      const res = await fetch(`${API_BASE}/dashboards/${selectedDashboard.id}/widgets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          widget_id: widgetId,
          position: { x: 0, y: selectedDashboard.widgets?.length || 0 }
        })
      })
      const dashboard = await res.json()
      if (!dashboard.error) {
        loadDashboard(selectedDashboard.id)
      }
    } catch (error) {
      console.error('Failed to add widget to dashboard:', error)
    }
  }

  const getWidgetIcon = (iconName: string) => {
    const icons: Record<string, string> = {
      assessment: '📊',
      show_chart: '📈',
      bar_chart: '📊',
      donut_large: '🍩',
      table_chart: '📋',
      map: '🗺️',
      speed: '⏱️',
      format_list_numbered: '📝'
    }
    return icons[iconName] || '📊'
  }

  const getSizeIcon = (sizeId: string) => {
    const icons: Record<string, string> = {
      small: '◻️',
      medium: '◼️',
      large: '⬛',
      wide: '▬',
      tall: '▮'
    }
    return icons[sizeId] || '◼️'
  }

  const renderWidgetPreview = (widget: Widget) => {
    const data = widget.data || {}

    switch (widget.widget_type) {
      case 'kpi':
        return (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#4A90E2' }}>
              {(data as { formatted?: string }).formatted || '$0'}
            </div>
            {(data as { comparison?: { value: number; direction: string } }).comparison && (
              <div style={{
                color: (data as { comparison: { direction: string } }).comparison.direction === 'up' ? '#10B981' : '#EF4444',
                fontSize: '14px',
                marginTop: '8px'
              }}>
                {(data as { comparison: { direction: string } }).comparison.direction === 'up' ? '↑' : '↓'}{' '}
                {(data as { comparison: { value: number } }).comparison.value}%
              </div>
            )}
          </div>
        )

      case 'line_chart':
        return (
          <div style={{ padding: '10px' }}>
            <svg viewBox="0 0 200 80" style={{ width: '100%', height: '60px' }}>
              <polyline
                fill="none"
                stroke="#4A90E2"
                strokeWidth="2"
                points="10,60 40,50 70,55 100,40 130,45 160,30 190,20"
              />
              <polyline
                fill="none"
                stroke="#10B981"
                strokeWidth="2"
                points="10,70 40,65 70,60 100,55 130,50 160,45 190,35"
              />
            </svg>
            <div style={{ fontSize: '10px', color: '#6B7280', textAlign: 'center' }}>
              Trend over time
            </div>
          </div>
        )

      case 'bar_chart':
        return (
          <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-around', height: '60px', padding: '10px' }}>
            {[80, 65, 50, 40, 30].map((h, i) => (
              <div
                key={i}
                style={{
                  width: '20%',
                  height: `${h}%`,
                  backgroundColor: '#4A90E2',
                  borderRadius: '4px 4px 0 0'
                }}
              />
            ))}
          </div>
        )

      case 'donut_chart':
        return (
          <div style={{ textAlign: 'center', padding: '10px' }}>
            <svg viewBox="0 0 100 100" style={{ width: '60px', height: '60px' }}>
              <circle cx="50" cy="50" r="40" fill="none" stroke="#4A90E2" strokeWidth="20" strokeDasharray="88 163" />
              <circle cx="50" cy="50" r="40" fill="none" stroke="#10B981" strokeWidth="20" strokeDasharray="63 188" strokeDashoffset="-88" />
              <circle cx="50" cy="50" r="40" fill="none" stroke="#F59E0B" strokeWidth="20" strokeDasharray="45 206" strokeDashoffset="-151" />
            </svg>
          </div>
        )

      case 'gauge':
        return (
          <div style={{ textAlign: 'center', padding: '10px' }}>
            <svg viewBox="0 0 100 60" style={{ width: '80px', height: '50px' }}>
              <path d="M 10 50 A 40 40 0 0 1 90 50" fill="none" stroke="#E5E7EB" strokeWidth="8" />
              <path d="M 10 50 A 40 40 0 0 1 70 15" fill="none" stroke="#4A90E2" strokeWidth="8" />
            </svg>
            <div style={{ fontSize: '16px', fontWeight: 'bold' }}>87%</div>
          </div>
        )

      case 'table':
        return (
          <div style={{ fontSize: '10px', padding: '5px' }}>
            <div style={{ display: 'flex', borderBottom: '1px solid #E5E7EB', fontWeight: 'bold', padding: '4px 0' }}>
              <span style={{ flex: 1 }}>Name</span>
              <span style={{ width: '50px' }}>Value</span>
            </div>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', padding: '4px 0', borderBottom: '1px solid #F3F4F6' }}>
                <span style={{ flex: 1, color: '#6B7280' }}>Item {i}</span>
                <span style={{ width: '50px' }}>${i}0,000</span>
              </div>
            ))}
          </div>
        )

      case 'list':
        return (
          <div style={{ fontSize: '10px', padding: '5px' }}>
            {[1, 2, 3].map(i => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', padding: '4px 0' }}>
                <span style={{
                  width: '18px',
                  height: '18px',
                  borderRadius: '50%',
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '10px',
                  marginRight: '8px'
                }}>
                  {i}
                </span>
                <span style={{ flex: 1 }}>Item {i}</span>
                <span style={{ color: '#6B7280' }}>${i}0k</span>
              </div>
            ))}
          </div>
        )

      case 'map':
        return (
          <div style={{ textAlign: 'center', padding: '10px', color: '#6B7280' }}>
            🗺️
            <div style={{ fontSize: '10px', marginTop: '5px' }}>Geographic Map</div>
          </div>
        )

      default:
        return (
          <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
            {widget.widget_type}
          </div>
        )
    }
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: '#F9FAFB' }}>
      <Header />

      <div style={{ padding: '24px', maxWidth: '1600px', margin: '0 auto' }}>
        {/* Page Header */}
        <div style={{ marginBottom: '24px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', margin: 0 }}>
            Widget Builder
          </h1>
          <p style={{ color: '#6B7280', marginTop: '4px' }}>
            Create custom widgets and dashboards for your analytics
          </p>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '8px', marginBottom: '24px', borderBottom: '1px solid #E5E7EB', paddingBottom: '8px' }}>
          {(['widgets', 'dashboards', 'templates'] as const).map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              style={{
                padding: '8px 16px',
                borderRadius: '8px 8px 0 0',
                border: 'none',
                backgroundColor: activeTab === tab ? '#4A90E2' : 'transparent',
                color: activeTab === tab ? 'white' : '#6B7280',
                cursor: 'pointer',
                fontWeight: activeTab === tab ? '600' : '400',
                textTransform: 'capitalize'
              }}
            >
              {tab}
            </button>
          ))}
        </div>

        {/* Widgets Tab */}
        {activeTab === 'widgets' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>My Widgets ({widgets.length})</h2>
              <button
                onClick={() => setIsCreatingWidget(true)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                + Create Widget
              </button>
            </div>

            {/* Widget Creation Form */}
            {isCreatingWidget && (
              <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '24px'
              }}>
                <h3 style={{ margin: '0 0 16px 0' }}>Create New Widget</h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                      Widget Name
                    </label>
                    <input
                      type="text"
                      value={newWidget.name}
                      onChange={e => setNewWidget({ ...newWidget, name: e.target.value })}
                      placeholder="Enter widget name"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                      Widget Type
                    </label>
                    <select
                      value={newWidget.widget_type}
                      onChange={e => setNewWidget({ ...newWidget, widget_type: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select type...</option>
                      {widgetTypes.map(type => (
                        <option key={type.id} value={type.id}>
                          {getWidgetIcon(type.icon)} {type.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                      Size
                    </label>
                    <select
                      value={newWidget.size}
                      onChange={e => setNewWidget({ ...newWidget, size: e.target.value })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    >
                      {widgetSizes.map(size => (
                        <option key={size.id} value={size.id}>
                          {getSizeIcon(size.id)} {size.name} ({size.cols}x{size.rows})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                      Metric
                    </label>
                    <select
                      onChange={e => setNewWidget({ ...newWidget, config: { ...newWidget.config, metric: e.target.value } })}
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select metric...</option>
                      {metrics.map(metric => (
                        <option key={metric.id} value={metric.id}>
                          {metric.name} ({metric.category})
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button
                    onClick={createWidget}
                    disabled={!newWidget.name || !newWidget.widget_type}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: newWidget.name && newWidget.widget_type ? '#10B981' : '#D1D5DB',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: newWidget.name && newWidget.widget_type ? 'pointer' : 'not-allowed',
                      fontWeight: '500'
                    }}
                  >
                    Create Widget
                  </button>
                  <button
                    onClick={() => setIsCreatingWidget(false)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#F3F4F6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Widgets Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
              {widgets.map(widget => (
                <div
                  key={widget.id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{ padding: '16px', borderBottom: '1px solid #F3F4F6' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{widget.name}</h3>
                        <span style={{
                          display: 'inline-block',
                          marginTop: '4px',
                          padding: '2px 8px',
                          backgroundColor: '#EFF6FF',
                          color: '#1D4ED8',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          {widget.widget_type}
                        </span>
                      </div>
                      <button
                        onClick={() => deleteWidget(widget.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#EF4444',
                          cursor: 'pointer',
                          fontSize: '18px'
                        }}
                      >
                        ×
                      </button>
                    </div>
                  </div>

                  <div style={{ minHeight: '100px' }}>
                    {renderWidgetPreview(widget)}
                  </div>

                  <div style={{ padding: '12px', backgroundColor: '#F9FAFB', display: 'flex', gap: '8px' }}>
                    <span style={{
                      padding: '4px 8px',
                      backgroundColor: '#E5E7EB',
                      borderRadius: '4px',
                      fontSize: '12px'
                    }}>
                      {widget.size}
                    </span>
                    {selectedDashboard && (
                      <button
                        onClick={() => addWidgetToDashboard(widget.id)}
                        style={{
                          marginLeft: 'auto',
                          padding: '4px 12px',
                          backgroundColor: '#4A90E2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          fontSize: '12px',
                          cursor: 'pointer'
                        }}
                      >
                        Add to Dashboard
                      </button>
                    )}
                  </div>
                </div>
              ))}

              {widgets.length === 0 && (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '48px',
                  backgroundColor: 'white',
                  borderRadius: '12px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📊</div>
                  <h3 style={{ margin: '0 0 8px 0' }}>No Widgets Yet</h3>
                  <p style={{ color: '#6B7280', margin: 0 }}>
                    Create your first widget or use a template to get started
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Dashboards Tab */}
        {activeTab === 'dashboards' && (
          <div>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
              <h2 style={{ fontSize: '20px', fontWeight: '600', margin: 0 }}>My Dashboards ({dashboards.length})</h2>
              <button
                onClick={() => setIsCreatingDashboard(true)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#4A90E2',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                + Create Dashboard
              </button>
            </div>

            {/* Dashboard Creation Form */}
            {isCreatingDashboard && (
              <div style={{
                backgroundColor: 'white',
                padding: '24px',
                borderRadius: '12px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                marginBottom: '24px'
              }}>
                <h3 style={{ margin: '0 0 16px 0' }}>Create New Dashboard</h3>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '16px' }}>
                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                      Dashboard Name
                    </label>
                    <input
                      type="text"
                      value={newDashboard.name}
                      onChange={e => setNewDashboard({ ...newDashboard, name: e.target.value })}
                      placeholder="Enter dashboard name"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '4px' }}>
                      Description
                    </label>
                    <input
                      type="text"
                      value={newDashboard.description}
                      onChange={e => setNewDashboard({ ...newDashboard, description: e.target.value })}
                      placeholder="Optional description"
                      style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>

                <div style={{ marginTop: '12px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={newDashboard.is_default}
                      onChange={e => setNewDashboard({ ...newDashboard, is_default: e.target.checked })}
                    />
                    <span style={{ fontSize: '14px' }}>Set as default dashboard</span>
                  </label>
                </div>

                <div style={{ display: 'flex', gap: '12px', marginTop: '16px' }}>
                  <button
                    onClick={createDashboard}
                    disabled={!newDashboard.name}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: newDashboard.name ? '#10B981' : '#D1D5DB',
                      color: 'white',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: newDashboard.name ? 'pointer' : 'not-allowed',
                      fontWeight: '500'
                    }}
                  >
                    Create Dashboard
                  </button>
                  <button
                    onClick={() => setIsCreatingDashboard(false)}
                    style={{
                      padding: '10px 20px',
                      backgroundColor: '#F3F4F6',
                      color: '#374151',
                      border: 'none',
                      borderRadius: '8px',
                      cursor: 'pointer'
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Dashboards Grid */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))', gap: '16px' }}>
              {dashboards.map(dashboard => (
                <div
                  key={dashboard.id}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                    border: selectedDashboard?.id === dashboard.id ? '2px solid #4A90E2' : '2px solid transparent'
                  }}
                >
                  <div style={{ padding: '20px' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                      <div>
                        <h3 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>
                          {dashboard.name}
                          {dashboard.is_default && (
                            <span style={{
                              marginLeft: '8px',
                              padding: '2px 8px',
                              backgroundColor: '#FEF3C7',
                              color: '#D97706',
                              borderRadius: '4px',
                              fontSize: '12px'
                            }}>
                              Default
                            </span>
                          )}
                        </h3>
                        {dashboard.description && (
                          <p style={{ color: '#6B7280', fontSize: '14px', margin: '8px 0 0 0' }}>
                            {dashboard.description}
                          </p>
                        )}
                      </div>
                      <button
                        onClick={() => deleteDashboard(dashboard.id)}
                        style={{
                          background: 'none',
                          border: 'none',
                          color: '#EF4444',
                          cursor: 'pointer',
                          fontSize: '20px'
                        }}
                      >
                        ×
                      </button>
                    </div>

                    <div style={{ marginTop: '16px', display: 'flex', alignItems: 'center', gap: '16px' }}>
                      <span style={{ color: '#6B7280', fontSize: '14px' }}>
                        {dashboard.widgets?.length || 0} widgets
                      </span>

                      <div style={{ marginLeft: 'auto', display: 'flex', gap: '8px' }}>
                        <button
                          onClick={() => loadDashboard(dashboard.id)}
                          style={{
                            padding: '8px 16px',
                            backgroundColor: selectedDashboard?.id === dashboard.id ? '#4A90E2' : '#F3F4F6',
                            color: selectedDashboard?.id === dashboard.id ? 'white' : '#374151',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '14px'
                          }}
                        >
                          {selectedDashboard?.id === dashboard.id ? 'Selected' : 'Select'}
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Dashboard Preview */}
                  {selectedDashboard?.id === dashboard.id && selectedDashboard.widgets_data && (
                    <div style={{
                      borderTop: '1px solid #F3F4F6',
                      padding: '16px',
                      backgroundColor: '#F9FAFB',
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, 1fr)',
                      gap: '8px'
                    }}>
                      {selectedDashboard.widgets_data.slice(0, 4).map(widget => (
                        <div
                          key={widget.id}
                          style={{
                            backgroundColor: 'white',
                            padding: '8px',
                            borderRadius: '6px',
                            border: '1px solid #E5E7EB'
                          }}
                        >
                          <div style={{ fontSize: '12px', fontWeight: '500', marginBottom: '4px' }}>{widget.name}</div>
                          {renderWidgetPreview(widget)}
                        </div>
                      ))}
                      {(selectedDashboard.widgets_data.length || 0) > 4 && (
                        <div style={{ textAlign: 'center', padding: '20px', color: '#6B7280' }}>
                          +{selectedDashboard.widgets_data.length - 4} more
                        </div>
                      )}
                    </div>
                  )}
                </div>
              ))}

              {dashboards.length === 0 && (
                <div style={{
                  gridColumn: '1 / -1',
                  textAlign: 'center',
                  padding: '48px',
                  backgroundColor: 'white',
                  borderRadius: '12px'
                }}>
                  <div style={{ fontSize: '48px', marginBottom: '16px' }}>📋</div>
                  <h3 style={{ margin: '0 0 8px 0' }}>No Dashboards Yet</h3>
                  <p style={{ color: '#6B7280', margin: 0 }}>
                    Create a dashboard to organize your widgets
                  </p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Templates Tab */}
        {activeTab === 'templates' && (
          <div>
            <h2 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '16px' }}>Widget Templates</h2>
            <p style={{ color: '#6B7280', marginBottom: '24px' }}>
              Pre-configured widgets you can add to your collection with one click
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '16px' }}>
              {templates.map(template => {
                const templateWidget = { ...template, id: template.id, data: {} } as Widget
                return (
                  <div
                    key={template.id}
                    style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                      overflow: 'hidden'
                    }}
                  >
                    <div style={{ padding: '16px', borderBottom: '1px solid #F3F4F6' }}>
                      <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '600' }}>{template.name}</h3>
                      <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                        <span style={{
                          padding: '2px 8px',
                          backgroundColor: '#EFF6FF',
                          color: '#1D4ED8',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          {template.widget_type}
                        </span>
                        <span style={{
                          padding: '2px 8px',
                          backgroundColor: '#F3F4F6',
                          color: '#6B7280',
                          borderRadius: '4px',
                          fontSize: '12px'
                        }}>
                          {template.size}
                        </span>
                      </div>
                    </div>

                    <div style={{ minHeight: '100px' }}>
                      {renderWidgetPreview(templateWidget)}
                    </div>

                    <div style={{ padding: '12px', backgroundColor: '#F9FAFB' }}>
                      <button
                        onClick={() => createWidgetFromTemplate(template)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: '#4A90E2',
                          color: 'white',
                          border: 'none',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          fontWeight: '500'
                        }}
                      >
                        + Add to My Widgets
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )}

        {/* Data Sources Info */}
        <div style={{
          marginTop: '32px',
          backgroundColor: 'white',
          borderRadius: '12px',
          padding: '24px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
        }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '18px' }}>Available Data Sources</h3>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px' }}>
            {dataSources.map(source => (
              <div
                key={source.id}
                style={{
                  padding: '12px 16px',
                  backgroundColor: '#F3F4F6',
                  borderRadius: '8px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}
              >
                <span style={{
                  width: '8px',
                  height: '8px',
                  borderRadius: '50%',
                  backgroundColor: '#10B981'
                }} />
                <div>
                  <div style={{ fontWeight: '500', fontSize: '14px' }}>{source.name}</div>
                  <div style={{ color: '#6B7280', fontSize: '12px' }}>{source.description}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
